import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import React from 'react';
import { cn } from '../../utils/cn';

/**
 * BadgeVariants CVA for StatusBadge component
 * Coinbase-styled badge with semantic color variants
 */
const badgeVariants = cva(
  'inline-flex items-center gap-2 rounded-pill px-3 py-1 text-caption-strong font-semibold transition-colors',
  {
    variants: {
      variant: {
        success: 'bg-semantic-up/15 text-semantic-up',
        error: 'bg-semantic-down/15 text-semantic-down',
        warning: 'bg-accent-yellow/20 text-accent-yellow',
        info: 'bg-primary/15 text-primary',
        muted: 'bg-surface-strong text-muted',
      },
    },
    defaultVariants: {
      variant: 'muted',
    },
  }
);

export interface StatusBadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Show a colored dot indicator before the content
   */
  dot?: boolean;
  /**
   * Custom color for the dot indicator (defaults to currentColor)
   */
  dotColor?: string;
  /**
   * Badge content
   */
  children?: ReactNode;
}

/**
 * StatusBadge Component
 * Displays status information with optional dot indicator
 * Uses Coinbase design system colors and typography
 */
const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(
  (
    {
      className,
      variant = 'muted',
      dot = false,
      dotColor,
      children,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {dot && (
        <span
          className="h-2 w-2 rounded-full flex-shrink-0"
          style={{
            backgroundColor: dotColor || 'currentColor',
          }}
        />
      )}
      {children}
    </div>
  )
);
StatusBadge.displayName = 'StatusBadge';

export { StatusBadge, badgeVariants };
export default StatusBadge;
